import { useUser } from '@clerk/react'
import { skipToken } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useLocation } from '#router'
import { Button } from '@/components/ui/button'
import { usePageTitle } from '@/hooks/use-document-title'
import { assertNever } from '@/lib/assert-never'
import { answersBeforeStep, stepDefaults } from '@/lib/audition/answers'
import {
  hasDismissedWelcome,
  markWelcomeDismissed,
} from '@/lib/audition/apply-flags'
import {
  forgetStoredApplyToken,
  readStoredApplyToken,
  storeApplyToken,
} from '@/lib/audition/apply-token'
import { WELCOME_VIDEO } from '@/lib/audition/intro-video'
import {
  isDraftExpiredError,
  RESUBMIT_WINDOW_DAYS,
  returningState,
} from '@/lib/audition/resubmit'
import { APPLY_TOKEN_PARAM } from '@/lib/audition/resume-url'
import type { AuditionAnswer, StepArtifacts } from '@/lib/audition/steps'
import {
  ARTIFACT_COLUMNS,
  contactStillOwed,
  currentStepFromStepIndex,
  getQuestionStep,
  stepIndexFromCurrentStep,
  TOTAL_WIZARD_STEPS,
  WIZARD_STEPS,
} from '@/lib/audition/steps'
import type { SubmissionAddress } from '@/lib/audition/submission-address'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import LoadingFallback from '../loading-fallback'
import { AccountStep, DoneScreen } from './account-step'
import { AnswerRecap } from './answer-recap'
import { STEP_ASIDES } from './asides'
import { BoostHub } from './boost-hub'
import type { ContactValues } from './contact-step'
import { ContactStep } from './contact-step'
import { IntroVideo } from './intro-video'
import { QuestionStep } from './question-step'
import { StageRail } from './stage-rail'
import { StepVideo } from './step-video'
import { WelcomeScreen } from './welcome-screen'

/**
 * A dismissible one-liner above the step, for news about the application
 * the applicant is looking at (a dead link, an expired draft). A status
 * line rather than a toast because it explains the form on screen, so it
 * has to stay until they have read it.
 *
 * Not itself a live region: it mounts with its message already inside,
 * which screen readers do not announce. The wizard's always-mounted
 * announcer carries the words to them instead.
 */
function StatusNotice({
  children,
  onDismiss,
}: {
  children: React.ReactNode
  onDismiss: () => void
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted px-4 py-3 text-sm">
      <p>{children}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}

export default function Wizard() {
  usePageTitle('Apply')
  const { searchParams, navigate } = useLocation()
  // Every token read/store/forget goes through apply-token's guarded helpers:
  // storage-blocked browsers (in-app webviews with DOM storage off, Safari
  // blocking cookies) throw from localStorage, and this read runs during
  // render. There the token lives in React state only — any full page load
  // loses it, which is why AccountStep carries it in Clerk's post-signup
  // redirect URL — instead of /apply rendering blank.
  const [token, setToken] = useState<string | null>(
    // `||`, not `??`: `/apply?token=` hands back '' — a string, so `??`
    // keeps it and never falls through to the token in localStorage. A
    // truncated email link would then land a returning applicant on the
    // contact form instead of their own resume (LG-1031).
    () => searchParams.get(APPLY_TOKEN_PARAM) || readStoredApplyToken() || null,
  )
  useEffect(() => {
    // Strip a `?token=` resume link from the address bar once it's been
    // captured into state: keeps the long-lived bearer token out of browser
    // history, and stops a dead URL token from re-triggering the
    // invalid-link notice on every refresh. Through the router (not raw
    // history.replaceState) so the router's location state updates too —
    // otherwise the header's Clerk sign-in buttons would keep re-embedding
    // the token via their pathname+search redirect URLs, and the entry's
    // scroll-restoration key would be wiped.
    const urlToken = searchParams.get(APPLY_TOKEN_PARAM)
    if (!urlToken) return
    // Persist before stripping: the email link is the ONLY carrier of this
    // token, so without this a refresh loses the pick-a-time and enrollment
    // surfaces entirely.
    storeApplyToken(urlToken)
    const cleaned = new URL(window.location.href)
    cleaned.searchParams.delete(APPLY_TOKEN_PARAM)
    navigate(cleaned.pathname + cleaned.search, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, [])
  // 0-based index into WIZARD_STEPS.
  const [stepIndex, setStepIndex] = useState(0)
  const [contact, setContact] = useState<Partial<ContactValues>>({})
  const [answers, setAnswers] = useState<
    Record<string, Record<string, AuditionAnswer>>
  >({})
  const [hydrated, setHydrated] = useState(false)
  // What the artifact boxes open with (Background's LeetCode link). Copied
  // out of the resume payload at hydration, like contact and answers,
  // because the payload itself does not outlive a trip back through Contact:
  // `start` mints a fresh token there, the resume query re-keys onto it,
  // and, disabled once hydrated, has nothing under the new key. Kept apart
  // from `answers` because it is stored apart: these are columns.
  const [artifacts, setArtifacts] = useState<StepArtifacts>({})
  const [resumeNoticeDismissed, setResumeNoticeDismissed] = useState(false)
  const [reopenedNoticeDismissed, setReopenedNoticeDismissed] = useState(false)
  // LG-1046: this visit met an expired draft, by any route — an emailed link
  // the server refused, `start` replacing the draft behind a retyped email,
  // or a write from a tab left open across day 60 (a step save, the account
  // link). A latch rather than a
  // reading of whichever route fired, so the notice has ONE lifetime (until
  // dismissed) and someone who takes two routes reads the news once.
  const [draftExpired, setDraftExpired] = useState(false)
  // LG-124: `start` met a live draft for this email that this browser could
  // not prove it holds, so the resume link went to the inbox instead of
  // coming back in the response. Holds the address the link was sent to.
  // Local state rather than a reading of `start.data`: the panel outlives
  // the mutation (a retry, a fresh start) and has its own way out.
  const [resumeEmailedTo, setResumeEmailedTo] = useState<string | null>(null)
  // Where focus goes when the panel arrives; the handoff itself waits until
  // the phase is derived, below.
  const resumeEmailedHeadingRef = useRef<HTMLHeadingElement>(null)
  const [expiredNoticeDismissed, setExpiredNoticeDismissed] = useState(false)
  // Where focus goes when a notice is dismissed. The notice unmounts with
  // focus on its own button, which would drop focus to <body> and send the
  // next Tab back to the top of the page; the step column is what follows.
  const stepColumnRef = useRef<HTMLDivElement>(null)
  const { isSignedIn, isLoaded: userLoaded } = useUser()

  // Finding 2 fix: a signed-in applicant who refreshes /apply without a
  // resume token (e.g. after the done screen already cleared it, or after
  // linking via an active session) must land on the done state, not a blank
  // contact form. Any row here means the submission is already linked to
  // this user, which only ever happens once its stage has advanced past
  // wizard_in_progress — so "has a row" and "past wizard_in_progress" are
  // equivalent, and no further stage check is needed.
  const myApplication = trpc.audition.myApplication.useQuery(undefined, {
    enabled: !!isSignedIn && !token,
  })
  // Gated on `isLoaded` too: Clerk reports `isSignedIn: undefined` until the
  // session resolves, so without it this reads "not signed in" for that first
  // window, the phase falls through to `step`, and an applicant who already
  // applied gets a flash of the contact form inviting them to do it again —
  // on exactly the no-token path LG-1031 exists to serve.
  const checkingExistingApplication =
    !token && (!userLoaded || (!!isSignedIn && myApplication.isLoading))
  const hasExistingApplication = !!isSignedIn && !token && !!myApplication.data

  // No token means no address — `skipToken`, not `{ token: '' }`. An empty
  // token type-checks as a `SubmissionAddress` but can never pass
  // `submissionAddressSchema` (`min(1)`), so the type would assert a validity
  // the value does not have, with only `enabled` keeping the server from
  // seeing it. Same sentinel, same fix, as `BoostHub`.
  //
  // The HYDRATION gate stays on `enabled` and must not move into the input.
  // Once hydrated (from a successful resume, or a fresh start that sets
  // hydrated immediately), the resume query has done its job and is disabled
  // so refetchOnWindowFocus etc. can't fire mid-wizard and clobber state —
  // but `resumedDone`/`returningApplication` below keep reading `resume.data`
  // afterwards. `enabled: false` holds the cache entry under the same key;
  // swapping to `skipToken` re-keys the query and drops that data, which
  // takes the done screen off an already-submitted resumed applicant.
  const resume = trpc.audition.get.useQuery(token ? { token } : skipToken, {
    enabled: !hydrated,
    retry: false,
  })

  // A resumed submission whose stage already moved past wizard_in_progress
  // (e.g. submitted from another tab/device, or a stale resume link reused
  // after completion) must land on the done state rather than mid-wizard.
  // Accepted race (do not "fix"): a fresh signup whose linking webhook beats
  // the post-redirect resume fetch lands here too and sees the returning
  // copy with today's date — spec classifies any resumed past-wizard token
  // as returning.
  const resumedDone =
    !!resume.data && resume.data.stage !== 'wizard_in_progress'
  // The wizard-level done state is always a RETURNING visitor: a fresh
  // completion renders DoneScreen from AccountStep/LinkAndDone instead.
  // Holding the row (not a boolean) hands its createdAt to the copy.
  const returningApplication = resumedDone
    ? resume.data
    : hasExistingApplication
      ? myApplication.data
      : undefined

  // The boost surfaces take a submission address (LG-1056): the emailed
  // token when this tab holds one, else the row id of the application
  // `myApplication` already loaded, read back through the session. This
  // screen is reachable signed-in with no token at all (cleared
  // localStorage, or a session that never held the emailed link), and the
  // id path is what keeps the surfaces addressable there without minting a
  // bearer token the session never needed (the LG-1031 latch this replaces).
  // A fresh object per render is fine: every consumer keys on
  // `submissionAddressKey`, never on identity, and React Query hashes it.
  const existingId = hasExistingApplication ? myApplication.data?.id : undefined
  const boostAddress: SubmissionAddress | null = token
    ? { token }
    : existingId
      ? { submissionId: existingId }
      : null

  const utils = trpc.useUtils()
  // Local override so "Start a new application" can drop the done screen
  // without waiting on a server round trip — the new row is only created
  // once they submit the contact step.
  const [startingNew, setStartingNew] = useState(false)
  // Local override so a successful resubmit lands on the plain "we got it"
  // screen immediately, without waiting on the invalidated query's refetch
  // to come back with the fresh resubmittedAt.
  const [justResubmitted, setJustResubmitted] = useState(false)
  const resubmit = trpc.audition.resubmit.useMutation({
    onSuccess: () => {
      setJustResubmitted(true)
      // Re-read so the cache carries the fresh resubmittedAt too — keeps
      // the quiet period accurate if the applicant refreshes.
      utils.audition.myApplication.invalidate()
    },
    onError: () => {
      toast.error("We couldn't resubmit your application — please try again.")
    },
  })

  const returning = justResubmitted
    ? 'received'
    : returningApplication
      ? returningState({
          stage: returningApplication.stage,
          submittedAt: returningApplication.submittedAt
            ? new Date(returningApplication.submittedAt)
            : null,
          resubmittedAt: returningApplication.resubmittedAt
            ? new Date(returningApplication.resubmittedAt)
            : null,
          createdAt: new Date(returningApplication.createdAt),
          now: new Date(),
        })
      : 'received'

  const showDoneScreen = !!returningApplication && !startingNew

  useEffect(() => {
    if (hydrated || !resume.data) return
    const contact: ContactValues = {
      email: resume.data.email,
      firstName: resume.data.firstName,
      lastName: resume.data.lastName,
      phone: resume.data.phone ?? '',
      linkedInUrl: resume.data.linkedInUrl ?? '',
      linkedInOptedOut: resume.data.linkedInOptedOut,
      acceptedTerms: resume.data.acceptedTerms,
      marketingConsent: resume.data.marketingConsent,
    }
    setContact(contact)
    setAnswers(resume.data.answers)
    // Walked, not named: a column the resume payload fills but hydration
    // forgets opens its box blank, and a blank box saved on Next is the
    // take-down of the link already stored.
    const resumed: StepArtifacts = {}
    for (const column of ARTIFACT_COLUMNS)
      resumed[column] = resume.data[column] ?? ''
    setArtifacts(resumed)
    // Resume lands on the next unfinished step — unless Contact still owes
    // an answer, as a draft saved before LG-1030 asked for a LinkedIn link
    // and the terms does. That one reopens Contact, and Next there goes
    // through `start`, which hands back the step the draft had reached.
    setStepIndex(
      contactStillOwed(contact).length === 0
        ? stepIndexFromCurrentStep(resume.data.currentStep)
        : 0,
    )
    setHydrated(true)
  }, [resume.data, hydrated])

  useEffect(() => {
    // Expired/invalid resume token: stop pointing back at a dead link so a
    // page refresh doesn't just re-trigger the same error. Only acts
    // pre-hydration — once hydrated, the resume query is disabled and a
    // stray/stale isError must never evict a token that's already proven
    // valid (e.g. a transient refetch failure mid-wizard).
    if (resume.isError && !hydrated) {
      forgetStoredApplyToken()
    }
  }, [resume.isError, hydrated])

  // The server refused to resume this link because the draft behind it is
  // past its window — a different message from a dead link, and not a
  // transient failure, so no hydration guard: the fact stays true.
  const resumeExpired = resume.isError && isDraftExpiredError(resume.error)
  useEffect(() => {
    // Latched because the error does not outlive its route: once `start`
    // mints a token for a different email the query re-keys, the error is
    // gone, and the notice used to go with it while still being read.
    if (resumeExpired) setDraftExpired(true)
  }, [resumeExpired])
  // `resumeExpired` is read directly as well so the notice is there on the
  // first frame, not one effect later.
  const showExpiredNotice =
    (resumeExpired || draftExpired) && !expiredNoticeDismissed
  const showDeadLinkNotice =
    resume.isError && !resumeExpired && !hydrated && !resumeNoticeDismissed

  const start = trpc.audition.start.useMutation({
    onSuccess: (result, variables) => {
      if (result.status === 'resume_emailed') {
        setResumeEmailedTo(variables.email)
        return
      }
      const {
        token: minted,
        currentStep,
        replacedExpiredDraft: replaced,
      } = result
      storeApplyToken(minted)
      setToken(minted)
      if (replaced) setDraftExpired(true)
      if (currentStep > 1) {
        // Reuse path: an in-progress submission for this email already
        // advanced past step 1 (e.g. a different device, or a prior
        // session).
        if (hydrated) {
          // Already hydrated this session (e.g. Back to the contact step,
          // edit, Next): local answers are current and the resume query is
          // permanently disabled, so waiting for it would strand the
          // applicant on the contact form. Jump to the server's step.
          setStepIndex(stepIndexFromCurrentStep(currentStep))
          return
        }
        // Not yet hydrated (the caller proved possession with a stored
        // token, e.g. a reload onto Contact): local state only has the
        // contact step — leave `hydrated` false so the resume query
        // fetches and hydrates the real answers/step instead of stranding
        // the applicant on step 1.
        return
      }
      setStepIndex(1)
      // Local state (contact + step) is already the source of truth for a
      // freshly-minted token — no resume fetch needed to "hydrate" it.
      setHydrated(true)
    },
    onError: () => {
      toast.error(
        'Something went wrong starting your application — please try again.',
      )
    },
  })

  const saveStep = trpc.audition.saveStep.useMutation({
    onSuccess: () =>
      setStepIndex((i) => Math.min(i + 1, TOTAL_WIZARD_STEPS - 1)),
    onError: (error) => {
      if (isDraftExpiredError(error)) {
        resetToFreshStart('expired')
        return
      }
      toast.error("We couldn't save your answers — please try again.")
    },
  })

  /**
   * Put the wizard back at a fresh start: no token, no answers, the contact
   * step.
   *
   * The one writer of that state, for both ways in. Two copies of it drifted
   * twice — the expiry one shipped without the `startingNew` guard, the
   * button's without the welcome write — so any new piece of wizard state a
   * fresh start must clear belongs here.
   *
   * - `'new'`: a returning applicant pressed "Start a new application", so
   *   their contact details clear too.
   * - `'expired'`: the draft expired mid-wizard (a tab left open across day
   *   60). Every token-addressed write past the contact step meets the same
   *   refusal — a step save, the account link — so none of
   *   them is a retry case, and the resume query that would otherwise notice
   *   is disabled once hydrated. Contact is kept so the retype is one click,
   *   and the expired notice latches, as on the link and retype routes.
   */
  function resetToFreshStart(reason: 'new' | 'expired') {
    // Evicted, not just dropped, so a reload does not resurrect the token.
    forgetStoredApplyToken()
    setToken(null)
    setAnswers({})
    setArtifacts({})
    // Defensive: a draft hydrated after this comes back through `start`,
    // which has already stored a whole Contact, so today nothing reopens
    // Contact from here. Cleared anyway so a fresh start owns all its state.
    setReopenedNoticeDismissed(false)
    setStepIndex(0)
    setHydrated(false)
    // A fresh start is a fresh contact form, not the "check your email"
    // panel left over from an earlier submit.
    setResumeEmailedTo(null)
    // Evicting the token enables `myApplication`; without this a signed-in
    // applicant with an older linked application would be bounced onto its
    // done screen.
    setStartingNew(true)
    // The reset produces exactly the shape `showWelcome` gates on — no token,
    // step 0, no done screen — and whoever reaches it has plainly been
    // welcomed; without this they would land on the welcome video.
    dismissWelcome()
    // Exhaustive, so a new way into a fresh start has to decide what it keeps.
    switch (reason) {
      case 'new':
        setContact({})
        break
      case 'expired':
        setDraftExpired(true)
        break
      default:
        assertNever(reason, 'Unhandled fresh-start reason')
    }
  }

  // Everything about "what is on screen at step N" comes off this one
  // lookup: which component renders, whether a video plays, whether an
  // aside is shown. There is deliberately no index arithmetic left here.
  const step = WIZARD_STEPS[stepIndex]
  const questionStep =
    step?.kind === 'question' ? getQuestionStep(step.id) : undefined

  // The welcome screen is a phase ahead of the wizard, not a step: it has
  // no rail marker and no step number. It is skipped for anyone resuming (a
  // token means they have been here) and for anyone who has already pressed
  // Get started, so a refresh mid-application never replays it.
  //
  // Keyed on that button press rather than on the welcome video's seen-state:
  // the video is marked seen when its player mounts, so a first-time visitor
  // who reloads while it is still buffering would lose this screen — and its
  // time expectation and "your progress is saved" promise — for good,
  // with no route back to it.
  const [welcomeDismissed, setWelcomeDismissed] = useState(hasDismissedWelcome)
  const showWelcome =
    !showDoneScreen && !welcomeDismissed && !token && stepIndex === 0

  /**
   * Settle the welcome phase — durably, and for this render.
   *
   * The one writer, because the phase lives in two places that have to move
   * together: `localStorage` so it survives a refresh, and state so this
   * render already knows. Splitting them is not hypothetical — the "Start a
   * new application" reset once shipped writing neither, which sent a
   * returning applicant to the welcome video from the button that promises
   * them the form.
   */
  function dismissWelcome() {
    markWelcomeDismissed()
    setWelcomeDismissed(true)
  }

  /**
   * Which of the wizard's five surfaces is on screen.
   *
   * Derived once, in the order the surfaces take precedence, because the
   * alternative is what this replaced: every surface re-deriving its own
   * negation of the others (`!showDoneScreen && !showWelcome && ...`), so
   * that adding a fifth surface meant finding all of them and getting each
   * one right, and missing one renders two surfaces at once. LG-124 added
   * the fifth one, `resume-emailed`, and proved the paragraph above: it
   * first shipped as `resumeEmailedTo` hand-negated at three sites, and the
   * aside column was the fourth site nobody found.
   *
   * `resume-emailed` sits directly above `step` so it only wins where the
   * panel used to render — a spinner, the welcome screen and the done
   * screen still take precedence over it, exactly as they did when it was a
   * branch inside the contact step.
   *
   * Note this is about WHICH surface renders, not how wide it is — the
   * container still asks `showDoneScreen`, so the narrow prose width is
   * already set on the frame where the done screen is known but its data is
   * still landing.
   */
  const phase: 'loading' | 'welcome' | 'done' | 'resume-emailed' | 'step' =
    (token && !hydrated && !resume.isError) || checkingExistingApplication
      ? 'loading'
      : showWelcome
        ? 'welcome'
        : showDoneScreen && returningApplication
          ? 'done'
          : resumeEmailedTo
            ? 'resume-emailed'
            : 'step'

  // Derived from the phase, not from `step` alone: an aside belongs to a
  // step of the wizard, and the surfaces that are not a step must not grow
  // a second column. This is the site the hand-negation missed — inert only
  // because no contact-step aside exists yet.
  const Aside = phase === 'step' && step ? STEP_ASIDES[step.id] : undefined

  /**
   * Focus for the `resume-emailed` surface, both ways.
   *
   * Arriving, the panel replaces the form the applicant was typing in, so
   * focus goes to its heading — otherwise it stays on the unmounted Next
   * button, drops to <body>, and a screen reader hears that nothing
   * changed. Leaving is the same problem mirrored: the button focus is
   * sitting on unmounts, and the next Tab starts from the top of the page.
   *
   * Neither handoff can happen in the click handler the way a dismissed
   * notice's does — the element focus is going to does not exist until the
   * render commits — so both wait on the phase actually changing, and one
   * effect owns the pair so they cannot drift apart.
   */
  const previousPhase = useRef(phase)
  useEffect(() => {
    if (phase === previousPhase.current) return
    if (phase === 'resume-emailed') resumeEmailedHeadingRef.current?.focus()
    else if (previousPhase.current === 'resume-emailed' && phase === 'step') {
      stepColumnRef.current?.focus()
    }
    previousPhase.current = phase
  }, [phase])

  /**
   * The step body for the current step.
   *
   * A switch with an `assertNever` default rather than four sibling `&&`
   * conditionals: the whole point of `WIZARD_STEPS` is that adding a step
   * is a data edit, and a new `AuditionStepKind` that nobody told this
   * component about would render a rail above an empty column with no way
   * forward. Now that particular omission is a compile error, here, at the
   * one place that must be told.
   *
   * It is NOT the only route to that empty column, and the `return null`s
   * below are the others: a step past `contact` with no token, or a
   * question step whose id has no entry in `QUESTION_STEPS`, both render
   * nothing. Those are phase bugs rather than step-list bugs — the wizard
   * cannot legitimately be past contact without a token — and hoisting the
   * token check above this switch is the open proposal that would make the
   * exhaustiveness claim total. Until then, read the guarantee as "every
   * step kind is handled", not "every path renders something".
   */
  function renderStep() {
    if (!step) return null
    switch (step.kind) {
      case 'contact':
        return (
          <ContactStep
            defaults={contact}
            isSubmitting={start.isPending}
            onSubmit={(values) => {
              setContact(values)
              // The held token is the proof that lets an edit-and-resubmit
              // from Back reuse the draft; without one, a matching draft
              // gets its link mailed (LG-124).
              start.mutate({ ...values, resumeToken: token ?? undefined })
            }}
          />
        )
      case 'question':
        // No token means the contact step has not been submitted, so there
        // is nothing to save answers against yet.
        if (!questionStep || !token) return null
        return (
          <QuestionStep
            // Remount per step: QuestionStep seeds its answers state from
            // `defaults` in a useState initializer, which only runs on
            // mount — without the key, switching steps reuses the mounted
            // instance and ignores the new step's hydrated defaults.
            key={questionStep.id}
            step={questionStep}
            // Both tolerant of a question that has moved between steps: the
            // answer is keyed by the step it was asked on when the row was
            // written, and `openToMoves` and `interviewTimeline` moved to
            // Goals. A draft resumed across that move still finds its own
            // answers in the boxes.
            defaults={stepDefaults(questionStep.id, answers)}
            before={answersBeforeStep(questionStep.id, answers)}
            artifactDefaults={artifacts}
            isSubmitting={saveStep.isPending}
            onBack={() => setStepIndex((i) => Math.max(i - 1, 0))}
            onSubmit={(stepAnswers, stepArtifacts) => {
              setAnswers((a) => ({
                ...a,
                [questionStep.id]: stepAnswers,
              }))
              // Kept locally too, so Back reopens the box with what they
              // pasted — the resume query that first filled it is disabled.
              setArtifacts((a) => ({ ...a, ...stepArtifacts }))
              saveStep.mutate({
                token,
                stepId: questionStep.id,
                answers: stepAnswers,
                artifacts: stepArtifacts,
                // They just finished this step, so the server's cursor
                // moves to the NEXT one.
                currentStep: currentStepFromStepIndex(stepIndex + 1),
              })
            }}
          />
        )
      case 'account':
        if (!token) return null
        return (
          <AccountStep
            token={token}
            contact={contact}
            onExpired={() => resetToFreshStart('expired')}
            answers={answers}
          />
        )
      default:
        return assertNever(step.kind, 'Unhandled wizard step kind')
    }
  }

  // A resumed draft that still owed Contact an answer (saved before LG-1030
  // asked for a LinkedIn link and the terms) was reopened there. The
  // applicant came back expecting their place, so the notice says their
  // answers are kept and what is still needed. Derived, not latched, from
  // the same rule that reopened it: it clears itself the moment Contact is
  // submitted whole, and a fresh start (hydration dropped) never shows it.
  const contactReopened = hydrated && contactStillOwed(contact).length > 0

  // The one status notice on screen, if any, in precedence order. The visible
  // notice and the announcer both read it, so a notice cannot be shown without
  // being announced, nor announced with different words. Only the step phase
  // renders notices — a spinner must not be narrated as a fresh start.
  // The "check your email" panel is a phase of its own, so it is covered by
  // the same test: a notice about the link they arrived on, or a fresh
  // start, would contradict it ("start fresh below" — there is no form
  // below).
  const activeNotice =
    phase !== 'step'
      ? null
      : showExpiredNotice
        ? {
            message: `Your earlier application expired — applications stay open for ${RESUBMIT_WINDOW_DAYS} days — so this is a fresh start.`,
            dismiss: () => setExpiredNoticeDismissed(true),
          }
        : showDeadLinkNotice
          ? {
              message:
                'Your resume link is no longer valid — no problem, you can start fresh below.',
              dismiss: () => setResumeNoticeDismissed(true),
            }
          : contactReopened && !reopenedNoticeDismissed
            ? {
                message:
                  'Your answers are saved. We just need your LinkedIn profile (or to know you don’t have one) and your agreement to the terms before you can finish.',
                dismiss: () => setReopenedNoticeDismissed(true),
              }
            : null

  return (
    <main className="flex min-h-screen w-full flex-col gap-8 px-4 py-12">
      {/* Done screen only. A returning applicant may be back weeks later and
          should still be able to re-watch, but they have nothing left to do,
          so it starts collapsed and stays out of the way. In the wizard
          itself the welcome screen and the step videos own their own
          players, each with its own seen-state. */}
      {showDoneScreen && (
        <IntroVideo source={WELCOME_VIDEO.source} defaultCollapsed />
      )}
      {/* The wizard is wider than the done screen because it carries the
          aside column; the done screen is a single narrow column of prose. */}
      <div
        data-testid="apply-content-column"
        className={cn(
          'mx-auto flex w-full flex-col gap-8',
          showDoneScreen ? 'max-w-2xl' : 'max-w-4xl',
        )}
      >
        {/* The status notices' one live region. Always mounted, so a notice
            arriving is a change to content that already exists — the only
            kind screen readers announce. The notices mount with their words
            already inside (the resume-link one on the same frame the step
            replaces the spinner), so on their own they are read by no one.
            `sr-only` takes it out of flow, leaving the column's gap alone. */}
        <p role="status" className="sr-only">
          {activeNotice?.message}
        </p>
        {/* A step's video plays above the rail, at full content width —
            it frames the whole step, not just the field column. Which step
            carries one is declared in WIZARD_STEPS. */}
        {/* Neither belongs over the "check your email" panel either: it is
            a dead end for this browser, not a step of the wizard — which is
            why it is a phase and not a flag. */}
        {phase === 'step' && step?.video && <StepVideo video={step.video} />}

        {phase === 'step' && <StageRail stepIndex={stepIndex} />}

        {phase === 'loading' ? (
          <LoadingFallback />
        ) : phase === 'welcome' ? (
          <WelcomeScreen video={WELCOME_VIDEO} onStart={dismissWelcome} />
        ) : phase === 'done' && returningApplication ? (
          <DoneScreen
            variant="returning"
            appliedAt={
              returningApplication.submittedAt ?? returningApplication.createdAt
            }
            resubmittedAt={returningApplication.resubmittedAt}
            actions={
              <div className="space-y-4">
                {/* The boost surfaces, on BOTH addressing paths: `boostAddress`
                    is the emailed token when there is one and the loaded
                    row's id under the session otherwise. They used to be
                    inlined here behind `token &&`, which made this whole block
                    vanish for a signed-in applicant with cleared localStorage
                    (LG-1031). Mounted unconditionally: the hub handles a null
                    address itself, so its header and the CV notice are on
                    screen even if the addressable surfaces can't load. */}
                <BoostHub address={boostAddress} />
                {returning === 'choice' && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Nothing has changed? Send us the same answers again and
                      we'll take another look.
                    </p>
                    <AnswerRecap answers={returningApplication.answers ?? {}} />
                    <Button
                      onClick={() =>
                        resubmit.mutate({
                          submissionId: returningApplication.id,
                        })
                      }
                      disabled={resubmit.isPending}
                    >
                      Resubmit this application
                    </Button>
                  </>
                )}
                {returning !== 'received' && (
                  <Button
                    variant="outline"
                    onClick={() => resetToFreshStart('new')}
                  >
                    Start a new application
                  </Button>
                )}
              </div>
            }
          />
        ) : phase === 'resume-emailed' && resumeEmailedTo ? (
          /* A surface of its own rather than a branch inside the contact
             step: nothing else on this frame belongs to a step, so nothing
             else may read as one. Focus arrives on the heading (the effect
             above), which is what announces the change — the words that
             matter are tied to it with `aria-describedby`, because a
             `role="status"` that mounts with its message already inside is
             read by no one, the same trap StatusNotice documents. */
          <div className="space-y-4">
            <h1
              ref={resumeEmailedHeadingRef}
              tabIndex={-1}
              aria-describedby="resume-emailed-detail"
              className="text-2xl font-semibold outline-none"
            >
              Check your email
            </h1>
            <p id="resume-emailed-detail">
              You already started an application with{' '}
              <strong>{resumeEmailedTo}</strong>. We sent a link there — open it
              to pick up where you left off, with the answers you saved before.
              What you typed just now was not saved.
            </p>
            <p className="text-sm text-muted-foreground">
              Nothing in your inbox? It can take a minute. Check your spam
              folder, or go back and start over with a different email.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                // The address was the problem, so it goes; the rest of what
                // they typed is kept for the retype.
                setContact((c) => ({ ...c, email: undefined }))
                setResumeEmailedTo(null)
              }}
            >
              Use a different email
            </Button>
          </div>
        ) : (
          /* The aside slot: the shell owns the column and its spacing so
             every page's aside lines up, and the page owns the content so
             the three of them can look nothing like each other. A step
             with no aside collapses to one column rather than reserving an
             empty gutter. Below `lg` there is one column and the aside sits
             above the fields. */
          <div
            className={cn(
              'grid items-start gap-8',
              Aside && 'lg:grid-cols-[minmax(0,1fr)_17rem]',
            )}
          >
            {/* Fields first in the DOM, aside second, even though the
                aside appears above on narrow screens: DOM order is reading
                and tab order, and an aside's <h2> ahead of the step's own
                <h1> is a heading-order violation for a screen reader. The
                explicit col-start/row-start pair on each column is what
                makes the visual layout independent of this order, and
                `order-first` still hoists the aside below `lg`. */}
            <div
              ref={stepColumnRef}
              // Focusable only by being sent here from a dismissed notice; it
              // never joins the tab order.
              tabIndex={-1}
              className="flex flex-col gap-8 outline-none lg:col-start-1 lg:row-start-1"
            >
              {activeNotice && (
                <StatusNotice
                  onDismiss={() => {
                    stepColumnRef.current?.focus()
                    activeNotice.dismiss()
                  }}
                >
                  {activeNotice.message}
                </StatusNotice>
              )}

              {renderStep()}
            </div>
            {Aside && (
              <div className="order-first lg:order-none lg:col-start-2 lg:row-start-1">
                <Aside />
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  )
}
